// AstriaVietnamCard.tsx
import React, { useState } from "react";
import i18n from "../../../i18n";
import { motion } from "framer-motion";
import {
  Share2,
  MessageCircleHeart,
  Copy,
  Check,
  Download,
} from "lucide-react";
import {
  SHELL_COLORS_ASTRIA_VIETNAM,
  ASTRIA_VIETNAM_HEADING_FONT,
} from "./AstriaVietnamShell";

// ─── Astria Vietnam — premium result-card primitives ───────────────────────
// Structurally cloned from AstriaIndiaV2Card.tsx, re-themed to Vietnam's
// soft pink/rose palette. No HighlightBadge/SignatureLine (India V3-only
// signature-layer fields — Vietnam's backend never sends these).

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// Subtle decorative geometric pattern (soft dot-ring), very low opacity,
// purely decorative, never obscures text.
const DecorativePattern: React.FC<{ accent: string }> = ({ accent }) => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    style={{
      position: "absolute",
      top: -20,
      right: -20,
      opacity: 0.07,
      pointerEvents: "none",
    }}
  >
    <circle cx="60" cy="60" r="50" fill="none" stroke={accent} strokeWidth="1.5" />
    <circle cx="60" cy="60" r="36" fill="none" stroke={accent} strokeWidth="1.5" />
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i * 30 * Math.PI) / 180;
      const x = 60 + 50 * Math.cos(angle);
      const y = 60 + 50 * Math.sin(angle);
      return <circle key={i} cx={x} cy={y} r="3" fill={accent} />;
    })}
  </svg>
);

interface AstriaVietnamCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: [string, string];
  children: React.ReactNode;
  delay?: number;
  shareText?: string;
  shareLabels?: ShareRowProps["labels"];
}

export const AstriaVietnamResultCard: React.FC<AstriaVietnamCardProps> = ({
  icon,
  title,
  subtitle,
  accent = [
    SHELL_COLORS_ASTRIA_VIETNAM.accent,
    SHELL_COLORS_ASTRIA_VIETNAM.primary,
  ],
  children,
  delay = 0,
  shareText,
  shareLabels,
}) => {
  return (
    <motion.div
      custom={delay}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      whileHover={{ y: -2 }}
      style={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(160deg, ${SHELL_COLORS_ASTRIA_VIETNAM.white} 0%, ${SHELL_COLORS_ASTRIA_VIETNAM.section} 100%)`,
        border: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
        borderRadius: 22,
        padding: 28,
        boxShadow:
          "0 10px 34px rgba(58,47,51,0.08), 0 2px 8px rgba(58,47,51,0.04)",
        transition: "box-shadow 0.3s ease, transform 0.3s ease",
      }}
    >
      <DecorativePattern accent={accent[0]} />

      <div className="flex items-center gap-3" style={{ marginBottom: 18 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: `linear-gradient(135deg, ${accent[0]} 0%, ${accent[1]} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            boxShadow: `0 6px 18px ${SHELL_COLORS_ASTRIA_VIETNAM.accentSoft}`,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
              fontFamily: ASTRIA_VIETNAM_HEADING_FONT,
              lineHeight: 1.25,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 12.5,
                color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>

      <AstriaVietnamShareRow
        text={shareText}
        accent={accent[0]}
        labels={shareLabels}
      />
    </motion.div>
  );
};

interface FieldBlockProps {
  label: string;
  value?: string;
  accent?: string;
}

export const AstriaVietnamFieldBlock: React.FC<FieldBlockProps> = ({
  label,
  value,
  accent = SHELL_COLORS_ASTRIA_VIETNAM.accent,
}) => {
  if (!value || !value.trim()) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          color: accent,
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <p
        style={{
          fontSize: 15,
          lineHeight: 1.75,
          color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </p>
    </div>
  );
};

export const AstriaVietnamDivider: React.FC = () => (
  <div
    style={{
      height: 1,
      background: SHELL_COLORS_ASTRIA_VIETNAM.cardBorder,
      margin: "18px 0",
    }}
  />
);

interface ShareRowProps {
  text?: string;
  accent?: string;
  labels?: {
    whatsApp?: string;
    instagram?: string;
    copy?: string;
    copied?: string;
    saveImage?: string;
  };
}

export const AstriaVietnamShareRow: React.FC<ShareRowProps> = ({
  text,
  accent = SHELL_COLORS_ASTRIA_VIETNAM.accent,
  labels,
}) => {
  const [copied, setCopied] = useState(false);
  const t = {
    whatsApp: labels?.whatsApp || "WhatsApp",
    instagram: labels?.instagram || "Instagram",
    copy: labels?.copy || "Copy",
    copied: labels?.copied || "Copied",
    saveImage: labels?.saveImage || "Save Image",
  };

  if (!text) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — silently ignore
    }
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleInstagram = () => {
    handleCopy();
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginTop: 20,
        paddingTop: 18,
        borderTop: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={handleWhatsApp}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{ paddingInline: 14, paddingBlock: 9, background: "#25D366", color: "#fff" }}
      >
        <Share2 className="w-3.5 h-3.5" />
        {t.whatsApp}
      </button>
      <button
        onClick={handleInstagram}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          background: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)",
          color: "#fff",
        }}
      >
        <Share2 className="w-3.5 h-3.5" />
        {t.instagram}
      </button>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderColor: SHELL_COLORS_ASTRIA_VIETNAM.cardBorder,
          color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
          background: SHELL_COLORS_ASTRIA_VIETNAM.white,
        }}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5" style={{ color: accent }} />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
        {copied ? t.copied : t.copy}
      </button>
      <button
        disabled
        title={i18n.t("tabErr.comingSoon")}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-all opacity-50 cursor-not-allowed"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderColor: SHELL_COLORS_ASTRIA_VIETNAM.cardBorder,
          color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
          background: SHELL_COLORS_ASTRIA_VIETNAM.white,
        }}
      >
        <Download className="w-3.5 h-3.5" />
        {t.saveImage}
      </button>
    </div>
  );
};

export const AstriaVietnamLoadingState: React.FC<{ label?: string }> = ({
  label = "Reading...",
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "70px 20px",
      gap: 16,
    }}
  >
    <div
      style={{
        width: 30,
        height: 30,
        border: `3px solid ${SHELL_COLORS_ASTRIA_VIETNAM.section}`,
        borderTopColor: SHELL_COLORS_ASTRIA_VIETNAM.accent,
        borderRadius: "50%",
      }}
      className="animate-spin"
    />
    <p style={{ fontSize: 13.5, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}>
      {label}
    </p>
  </div>
);

export const AstriaVietnamErrorState: React.FC<{
  message: string;
  onRetry: () => void;
  retryLabel?: string;
}> = ({ message, onRetry, retryLabel = "Try Again" }) => (
  <div
    style={{
      padding: "18px 20px",
      background: "#FBEDE9",
      border: "1px solid #E8C6BB",
      borderRadius: 14,
      color: "#95493A",
      fontSize: 14,
    }}
  >
    {message}
    <button
      onClick={onRetry}
      style={{
        display: "block",
        marginTop: 10,
        color: SHELL_COLORS_ASTRIA_VIETNAM.accent,
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      {retryLabel}
    </button>
  </div>
);

export const AstriaVietnamChatCta: React.FC<{
  onClick: () => void;
  label?: string;
}> = ({ onClick, label = "Continue in Chat" }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02]"
    style={{
      marginTop: 24,
      width: "100%",
      background: `linear-gradient(135deg, ${SHELL_COLORS_ASTRIA_VIETNAM.accent} 0%, ${SHELL_COLORS_ASTRIA_VIETNAM.primary} 100%)`,
      boxShadow: `0 6px 20px ${SHELL_COLORS_ASTRIA_VIETNAM.accentSoft}`,
      paddingTop: 14,
      paddingBottom: 14,
    }}
  >
    <MessageCircleHeart className="w-4 h-4" />
    {label}
  </button>
);

// ─── Compatibility-specific: score badge ───────────────────────────────────
// Only rendered when the backend actually populates compatibility_score
// (omitted entirely when partner DOB wasn't available — see
// backend/helper/vietnam/vietnamCompatibility.js) — never fabricate a
// score client-side.
export const CompatibilityScoreBadge: React.FC<{
  score?: number;
  label?: (score: number) => string;
}> = ({ score, label = (s) => `${s}/100` }) => {
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        paddingInline: 14,
        paddingBlock: 7,
        marginBottom: 16,
        background: SHELL_COLORS_ASTRIA_VIETNAM.section,
        border: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
        fontSize: 13,
        fontWeight: 700,
        color: SHELL_COLORS_ASTRIA_VIETNAM.accent,
      }}
    >
      {label(score)}
    </div>
  );
};
